// The AI provider's model, picked from the provider's own list where it has
// one, typed where it does not (85.6). An administrator should not need to
// know a model's exact id before Test connection can say it is wrong.
import { useEffect, useState } from 'react';
import { fieldClass } from '@/components/ui/field';
import { listLlmModels } from '@/lib/useAdmin';

const OTHER = '__other__';

type ListState =
  | { state: 'asking' }
  | { state: 'listed'; models: string[] }
  | { state: 'unlisted'; reason: string };

export function ModelPicker({
  provider,
  endpoint,
  apiKey,
  token,
  value,
  onChange,
}: {
  provider: string;
  endpoint: string;
  apiKey: string;
  token: string | undefined;
  value: string;
  onChange: (model: string) => void;
}) {
  const [list, setList] = useState<ListState>({ state: 'asking' });
  const [typing, setTyping] = useState(false);

  // Ask once the form settles: a typed endpoint or key changes what the
  // provider will list, but not on every keystroke.
  useEffect(() => {
    if (!token || !provider) return;
    let current = true;
    setList({ state: 'asking' });
    const handle = window.setTimeout(() => {
      void listLlmModels(
        { provider, endpoint: endpoint || undefined, api_key: apiKey || undefined },
        token,
      ).then((r) => {
        if (!current) return;
        if (!r.ok) setList({ state: 'unlisted', reason: r.message });
        else if (r.data.models.length === 0)
          setList({ state: 'unlisted', reason: r.data.reason ?? 'the provider gave no list' });
        else setList({ state: 'listed', models: r.data.models });
      });
    }, 500);
    return () => {
      current = false;
      window.clearTimeout(handle);
    };
  }, [provider, endpoint, apiKey, token]);

  const models = list.state === 'listed' ? list.models : [];
  const offered = models.includes(value);
  const showText = list.state !== 'listed' || typing || (value !== '' && !offered);

  return (
    <div className="block text-sm">
      <label htmlFor="ai-model" className="text-tres-text-secondary">
        Model
      </label>
      {list.state === 'listed' && (
        <select
          aria-label="Model from the provider's list"
          className={fieldClass('normal', 'mt-1 w-full')}
          value={showText ? OTHER : value}
          onChange={(e) => {
            if (e.target.value === OTHER) {
              setTyping(true);
              return;
            }
            setTyping(false);
            onChange(e.target.value);
          }}
        >
          {value === '' && !typing && <option value="">Choose a model</option>}
          {models.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
          <option value={OTHER}>Another model (type its name)</option>
        </select>
      )}
      {showText && (
        <input
          id="ai-model"
          className={fieldClass('normal', 'mt-1 w-full')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="The model's name as the provider writes it"
        />
      )}
      <span className="mt-1 block text-xs text-tres-text-secondary" role="status">
        {list.state === 'asking'
          ? 'Asking the provider which models it offers…'
          : list.state === 'listed'
            ? `${models.length} model${models.length === 1 ? '' : 's'} offered by the provider.`
            : `No list of models: ${list.reason.replace(/\.$/, '')}. Type the model's name.`}
      </span>
    </div>
  );
}
